import { transformCSVtoJSONV2,transformCSVtoJSON } from '@/utils/csvtojson';
import Ajv from "ajv";
import addFormats from "@/utils/custom-ajv-formats";
import API_FILES from '@/services/api/files.js';
import { DEX_TYPE } from "@/config/constants";
import { hasProp } from "@/utils/schema";
import { feature, features } from '@/utils/feature-verifier';
import { ajvErrorMapping } from '@/utils/ajv-error-mapping/index';
import ERROR_MESSAGE_COPIES from '@/constants/error-messages/index';

export const ATTACHMENT_FIELD_KEY = 'attachments';

const ajv = new Ajv({ allErrors: true, verbose: true });
addFormats(ajv)

export function HasSufficientMatchingAttachments(shareDataElement) {
    const matchingAttachment = shareDataElement?.attachments?.filter((attachment) => shareDataElement?.requiredAttachments?.includes(attachment.path)).length;
    return matchingAttachment >= shareDataElement?.requiredAttachments?.length;
}

export function IsPathMismatch(attachmentFiles, requiredAttachments) {
    return !requiredAttachments?.includes(attachmentFiles[0].path);
}

export function HasDuplicatePath(attachmentFiles, attachments) {
    return attachmentFiles.some((file) => attachments?.some((attachmentFile) => attachmentFile.path === file.path));
}

export function HandleAttachmentFiles(files, attachmentFiles) {
    if (files && attachmentFiles) return [...attachmentFiles, ...files];
}

export async function ReadJSON(file) {
    const fileReader = new FileReader();
    const content = await new Promise((resolve, reject) => {
        fileReader.onload = e => resolve(e.target.result);
        fileReader.onerror = e => reject(new Error('Failed to read file'));
        fileReader.readAsText(file, "utf-8");
    });

    const parsed = JSON.parse(content);
    // Basic payload structure validation
    if (parsed === null || parsed === undefined) {
        throw new Error("Invalid structure");
    }
    
    // Convert to array if single object
    const jsonArray = Array.isArray(parsed) ? parsed : [parsed];
    
    return jsonArray;
}

export function JSONParsedCheck(parsed) {
    if (!parsed || (Array.isArray(parsed) && parsed.length === 0)) {
        throw new Error('Empty payload');
    }
    return Array.isArray(parsed) ? parsed : [parsed];
}

export async function ReadCSV(file, schema,isErrorMessageEnhancement) {
    const fileReader = new FileReader();
    const content = await new Promise((resolve, reject) => {
        fileReader.onload = e => resolve(e.target.result);
        fileReader.onerror = e => reject(e);
        fileReader.readAsText(file, "utf-8");
    });
    
    // Get both the converted JSON and the metadata
    if(isErrorMessageEnhancement){
        const { convertedJSON, rowMetadata } = await transformCSVtoJSONV2(content, schema);
        
        // Store the metadata in a global variable or attach it to the result in a way that won't affect validation
        // This allows us to access the metadata for error reporting without affecting validation
        const result = ConvertedJSON(convertedJSON);
        
        // Attach metadata in a non-enumerable property so it won't be included in validation
        if (result && Array.isArray(result)) {
            result.forEach((item, index) => {
                if (rowMetadata && rowMetadata[index]) {
                    // Use defineProperty to make it non-enumerable
                    Object.defineProperty(item, '__metadata', {
                        value: rowMetadata[index],
                        enumerable: false, // This makes it invisible to JSON.stringify and most iterators
                        configurable: true,
                        writable: true
                    });
                }
            });
        }

        return result;
    } else {
        const { convertedJSON } = await transformCSVtoJSON(content, schema);
        return ConvertedJSON(convertedJSON)
    }
}

export function ConvertedJSON(converted) {
    return converted?.map(item => {
        return item.attachments && item.attachments.length > 0
            ? {
                ...item,
                attachments: item.attachments
                    .filter(attachment => attachment)
                    .filter(value => Object.keys(value).length !== 0),
            }
            : { ...item };
    });
}

export function GetAttachmentFiles(json, dex) {
    let attachments = [];
    let attachmentFiles = [];

    if ((dex === DEX_TYPE.SGHEALTHDEX || dex === DEX_TYPE.SGBUILDEX)) {
        json.forEach(event => {
            let resultAttachments = GetAttachments(attachments, event);
            attachmentFiles = resultAttachments.map(attachment => attachment.filename);
        });
    }
    else {
        json.forEach(event => {
            if (event.attachments && event.attachments.length > 0) {
                attachmentFiles = [...attachmentFiles, ...event.attachments.map(attachment => attachment.filename)];
            }
        });
    }
    return [...new Set(attachmentFiles.filter(item => item))];
}

export function ContainAttachments(dataElement) {
    if (!dataElement?.schema) {
        return false;
    }

    if (dataElement?.schema?.anyOf) {
        for (let subSchema of dataElement.schema.anyOf) {
            if (hasProp(subSchema, 'attachments')) {
                return true;
            }
        }
        return false;
    } else {
        return hasProp(dataElement.schema, 'attachments');
    }
}

export function FindAttachments(attachments = [], requiredAttachment = []) {
    let matchingAttachments = [];
    let nonMatchingAttachments = [];
    attachments.forEach((attachment) => {
        if (requiredAttachment.includes(attachment.path)) {
            matchingAttachments.push(attachment);
        } else {
            nonMatchingAttachments.push(attachment);
        }
    });

    return {
        matchingAttachments,
        nonMatchingAttachments
    };
}
export function CombineAttachment(shareDataElement) {
    const attachments = shareDataElement?.attachments || []
    const requiredAttachments = shareDataElement?.requiredAttachments || []
    const optionalAttachments = attachments
        .filter(attachment => !requiredAttachments.includes(attachment.path))
        .map(attachment => attachment.path);

    return {
        combineAttachments: [...requiredAttachments, ...optionalAttachments],
        optionalAttachments: optionalAttachments,
    }
}

export function HandleDownloadSingleFileFunction(item, shareDataElement) {
    const fileToDownload = shareDataElement?.attachments.find((attachment) => attachment.path === item);

    const link = document.createElement('a');
    link.href = fileToDownload.preview;
    link.download = fileToDownload.path;
    link.dispatchEvent(new MouseEvent('click'));
}

// Find the starting line of an item in the file content
// Track the current item index to handle multiple items in an array
let currentItemIndex = 0;
let lastFoundPositions = [];

export function findItemStartLine(item, allLines) {
    // Look for the first field of the item in the file
    const firstField = Object.keys(item)[0];
    const firstValue = item[firstField];
    
    // Determine the search start position based on the current item index
    let searchStartPos = 0;
    if (currentItemIndex > 0 && lastFoundPositions[currentItemIndex - 1] !== undefined) {
        // Start searching from just after the previous item's position
        searchStartPos = lastFoundPositions[currentItemIndex - 1] + 1;
    }
    
    // Search for this item starting from the appropriate position
    for (let i = searchStartPos; i < allLines.length; i++) {
        const line = allLines[i];
        if (line.includes(firstField) && line.includes(firstValue)) {
            // Store this position for the next item
            lastFoundPositions[currentItemIndex] = i;
            // Increment the item index for the next call
            currentItemIndex++;
            return i;
        }
    }
    
    // If not found from the search start position, try from the beginning
    if (searchStartPos > 0) {
        for (let i = 0; i < searchStartPos; i++) {
            const line = allLines[i];
            if (line.includes(firstField) && line.includes(firstValue)) {
                lastFoundPositions[currentItemIndex] = i;
                currentItemIndex++;
                return i;
            }
        }
    }
    
    return -1;
}

// Get line numbers for each field in an item, including nested fields
export function getFieldLineNumbers(allLines, startLine, endLine) {
    const fieldLineNumbers = {};
    
    // Track the current context while parsing the JSON structure
    const context = {
        arrayStack: [], // Stack of arrays we're currently inside
        arrayIndices: {}, // Current index for each array
        objectStack: [], // Stack of objects we're currently inside
        inArrayItem: false, // Whether we're inside an array item
        currentIndentation: -1, // Current indentation level
    };
    
    // Regular expressions for parsing JSON structure
    const arrayStartRegex = /^\s*"([^"]+)":\s*\[/;
    const arrayStartWithObjectRegex = /^\s*"([^"]+)":\s*\[\s*\{/; // Array with object on same line
    const arrayEndRegex = /^\s*\]\s*(,)?$/;
    const objectStartRegex = /^\s*\{\s*$/;
    const objectEndRegex = /^\s*\}\s*(,)?$/;
    const fieldRegex = /^\s*"([^"]+)":\s*(.+?)(,)?$/;
    
    // First pass: Scan for all field declarations regardless of nesting
    // This ensures we capture all fields even if they're after complex structures
    for (let i = startLine; i < endLine; i++) {
        const line = allLines[i] || '';
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;
        
        // Match any field declaration pattern
        const fieldMatch = trimmedLine.match(/^\s*"([^"]+)":\s*(.+?)(,)?$/);
        if (fieldMatch) {
            const fieldName = fieldMatch[1];
            if (!['__rowNumber', '__cellMap'].includes(fieldName)) {
                // Store the basic field name to line number mapping
                fieldLineNumbers[fieldName] = i;
            }
        }
    }
    
    // Second pass: Process the JSON structure to build nested paths
    for (let i = startLine; i < endLine; i++) {
        const line = allLines[i] || '';
        const trimmedLine = line.trim();
        if (!trimmedLine) continue;
        
        // Calculate indentation level to help determine structure
        const indentation = line.search(/\S|$/);
        context.currentIndentation = indentation;
        
        // Check for array start with object on same line: "array_name": [{
        const arrayStartWithObjectMatch = trimmedLine.match(arrayStartWithObjectRegex);
        if (arrayStartWithObjectMatch) {
            const arrayName = arrayStartWithObjectMatch[1];
            if (!['__rowNumber', '__cellMap'].includes(arrayName)) {
                // Store the line number for the array itself
                fieldLineNumbers[arrayName] = i;
                
                // Push this array onto the stack and initialize its index counter
                context.arrayStack.push(arrayName);
                context.arrayIndices[arrayName] = 0; // First item starts on this line
                context.inArrayItem = true;
                
                // Store the line number for the first array item
                const arrayItemPath = `${arrayName}/0`;
                const arrayItemDotPath = `${arrayName}.0`;
                
                fieldLineNumbers[arrayItemPath] = i;
                fieldLineNumbers[arrayItemDotPath] = i;
            }
            continue;
        }
        
        // Check for array start: "array_name": [
        const arrayStartMatch = trimmedLine.match(arrayStartRegex);
        if (arrayStartMatch && !arrayStartWithObjectMatch) {
            const arrayName = arrayStartMatch[1];
            if (!['__rowNumber', '__cellMap'].includes(arrayName)) {
                // Store the line number for the array itself
                fieldLineNumbers[arrayName] = i;
                
                // Push this array onto the stack and initialize its index counter
                context.arrayStack.push(arrayName);
                context.arrayIndices[arrayName] = -1; // Will be incremented when we find items
            }
            continue;
        }
        
        // Check for array end: ]
        if (trimmedLine.match(arrayEndRegex) && context.arrayStack.length > 0) {
            // Pop the current array from the stack
            context.arrayStack.pop();
            context.inArrayItem = false;
            continue;
        }
        
        // Check for object start: {
        if (trimmedLine.match(objectStartRegex)) {
            if (context.arrayStack.length > 0) {
                // This is likely the start of an array item
                const currentArray = context.arrayStack[context.arrayStack.length - 1];
                context.arrayIndices[currentArray]++;
                context.inArrayItem = true;
                
                // Store the line number for this array item
                const currentIndex = context.arrayIndices[currentArray];
                const arrayItemPath = `${currentArray}/${currentIndex}`;
                const arrayItemDotPath = `${currentArray}.${currentIndex}`;
                
                fieldLineNumbers[arrayItemPath] = i;
                fieldLineNumbers[arrayItemDotPath] = i;
            }
            continue;
        }
        
        // Check for object end: }
        if (trimmedLine.match(objectEndRegex)) {
            if (context.inArrayItem && indentation <= 8) {
                // This is likely the end of an array item
                context.inArrayItem = false;
            }
            continue;
        }
        
        // Match field declarations: "field_name": value
        const fieldMatch = trimmedLine.match(fieldRegex);
        if (fieldMatch) {
            const fieldName = fieldMatch[1];
            if (!['__rowNumber', '__cellMap'].includes(fieldName)) {
                if (context.inArrayItem && context.arrayStack.length > 0) {
                    // This is a field within an array item
                    const currentArray = context.arrayStack[context.arrayStack.length - 1];
                    const currentIndex = context.arrayIndices[currentArray];
                    
                    // Store the field path with both slash and dot notation
                    const arrayFieldPath = `${currentArray}/${currentIndex}/${fieldName}`;
                    const arrayFieldDotPath = `${currentArray}.${currentIndex}.${fieldName}`;
                    
                    fieldLineNumbers[arrayFieldPath] = i;
                    fieldLineNumbers[arrayFieldDotPath] = i;
                }
                // We already stored the top-level field in the first pass
            }
        }
        
        // Check for inline array items - matches patterns like: [{...}, or , {...}
        const inlineArrayItemMatch = trimmedLine.match(/^\s*(,\s*)?\{/);
        if (inlineArrayItemMatch && context.arrayStack.length > 0 && !context.inArrayItem) {
            const currentArray = context.arrayStack[context.arrayStack.length - 1];
            context.arrayIndices[currentArray]++;
            context.inArrayItem = true;
            
            // Store the line number for this array item
            const currentIndex = context.arrayIndices[currentArray];
            const arrayItemPath = `${currentArray}/${currentIndex}`;
            const arrayItemDotPath = `${currentArray}.${currentIndex}`;
            
            fieldLineNumbers[arrayItemPath] = i;
            fieldLineNumbers[arrayItemDotPath] = i;
        }
    }
    
    return fieldLineNumbers;
}

// Helper function to get the current path based on the path stack
function getCurrentPath(pathStack, arrayIndices) {
    if (pathStack.length === 0) return '';
    
    return pathStack.reduce((path, current, index) => {
        if (index > 0) path += '.';
        
        if (current.type === 'array') {
            const arrayIndex = arrayIndices[current.name];
            if (arrayIndex >= 0) {
                return path + current.name + '.' + arrayIndex;
            }
            return path + current.name;
        } else {
            return path + current.name;
        }
    }, '');
}

// Validate a single item
function validateSingleItem(item, validate, schema, isCSV, fileContent, apiSpecUrl) {
    const isValid = validate(item);
    
    if (isValid) {
        return {
            isValid: true,
            errors: {}
        }
    }

    // Use the actual file content if provided, otherwise fallback to JSON.stringify
    const fullContent = fileContent || JSON.stringify([item], null, 2);
    const allLines = fullContent.split('\n');
    
    // Find this item in the file content
    const itemString = JSON.stringify(item, null, 2);
    const itemLines = itemString.split('\n');
    
    // Find where this item starts in the file
    const itemStartLine = findItemStartLine(item, allLines);
    const itemEndLine = itemStartLine + itemLines.length;
        
    // Get line numbers for each field
    // For JSON files, we need to add 1 to match editor line numbers (1-based)
    const fieldLineNumbers = getFieldLineNumbers(allLines, itemStartLine, itemEndLine);
    
    // Adjust line numbers to be 1-based for editor compatibility
    const adjustedFieldLineNumbers = {};
    Object.keys(fieldLineNumbers).forEach(key => {
        adjustedFieldLineNumbers[key] = fieldLineNumbers[key] + 1;
    });
    console.log('adjustedFieldLineNumbers', adjustedFieldLineNumbers)
    // Get validation errors with line numbers
    // Access cellMap from the non-enumerable __metadata property if available
    const cellMap = item.__metadata ? item.__metadata.__cellMap : undefined;
    
    const errors = ajvErrorMapping(validate.errors, schema, item, {
        fieldLineNumbers: adjustedFieldLineNumbers, // Use the adjusted line numbers
        isCSV,
        cellMap: cellMap
    }, apiSpecUrl);
    
    // No need to clean the item since metadata is now stored as non-enumerable properties
    // and won't be included in validation or serialization
    
    return {
        isValid: false,
        errors,
    };
}

export function ValidateDataFunction(json, schema) {
    const validate = ajv.compile(schema);
    return json.every(item => validate(item));
}

export function ValidatePayloadFunction(json, schema, isCSV = false, fileContent = '', apiSpecUrl ='', showApiSpecUrl = true) {
    const validate = ajv.compile(schema);
    
    try {
        // Reset the item index counter before processing a new batch
        currentItemIndex = 0;
        lastFoundPositions = [];
        
        // Validate each item in the JSON array
        const validationResults = json.map(item => 
            validateSingleItem(item, validate, schema, isCSV, fileContent, apiSpecUrl)
        );

        const hasErrors = validationResults.some(result => !result?.isValid);
        return {
            isValid: !hasErrors,
            errors: hasErrors ? validationResults.map(({structureErrorMessage, errors}) => ({
                structureErrorMessage,
                errors
            })) : [],
        }
    } catch(err) {
        if (err.name === "PayloadStructureError") {
            return {
                isValid: false,
                structureErrorMessage: showApiSpecUrl && apiSpecUrl?.length > 0 ? err.message : "The data structure of the file is incorrect. Please upload a file with a structure that aligns with schema.",
            };
        }
        return {
            isValid: false,
            structureErrorMessage: "Unexpected Error occurred",
        };
    }
}

export function ValidateAttachmentsFunction(json) {
    const validate = json.map((item) => {
        const valueArr = item.attachments?.map(function (item) { return item.filename });
        return valueArr?.some(function (item, id) {
            return valueArr.indexOf(item) != id;
        });
    });
    return validate.includes(true);
}

export const GetAttachments = (attachments, payloadObj) => {
    let resultAttachments = attachments;
    if (payloadObj[ATTACHMENT_FIELD_KEY] && payloadObj[ATTACHMENT_FIELD_KEY].length > 0) {
        const payloadAttachments = payloadObj[ATTACHMENT_FIELD_KEY];
        for (const payloadAttachment of payloadAttachments) {
            if (resultAttachments.findIndex(x => x.filename === payloadAttachment.filename) === -1) {
                resultAttachments.push(payloadAttachment);
            }
        }
    }

    for (const objKey in payloadObj) {
        if (payloadObj[objKey] && typeof payloadObj[objKey] === 'object' && !Array.isArray(payloadObj[objKey])) {
            resultAttachments = GetAttachments(resultAttachments, payloadObj[objKey]);
        }
        else if (payloadObj[objKey] && typeof payloadObj[objKey] === 'object' && Array.isArray(payloadObj[objKey])) {
            for (const item of payloadObj[objKey]) {
                resultAttachments = GetAttachments(resultAttachments, item);
            }
        }
    }

    return resultAttachments;

}

export const AssignUploadingAttachments = (uploadedAttachments, json) => {
    let resultJSON = json;

    if (json[ATTACHMENT_FIELD_KEY] && json[ATTACHMENT_FIELD_KEY].length > 0) {
        const payloadAttachments = json[ATTACHMENT_FIELD_KEY];
        json[ATTACHMENT_FIELD_KEY] = uploadedAttachments.filter((item) => payloadAttachments?.some((val) => val.filename === item.name));
    }

    for (const objKey in json) {
        if (json[objKey] && typeof json[objKey] === 'object' && !Array.isArray(json[objKey])) {
            json[objKey] = AssignUploadingAttachments(uploadedAttachments, json[objKey]);
        }
        else if (objKey !== ATTACHMENT_FIELD_KEY && json[objKey] && typeof json[objKey] === 'object' && Array.isArray(json[objKey])) {
            json[objKey] = json[objKey].map((item) => AssignUploadingAttachments(uploadedAttachments, item));
        }
    }
    return resultJSON;
};

export const UploadAttachments = async (jsonObj) => {
    let errors = [];
    let resultJSON = { ...jsonObj };
    if (jsonObj[ATTACHMENT_FIELD_KEY] && jsonObj[ATTACHMENT_FIELD_KEY].length > 0) {
        const [error, uploadFilesResult] = await API_FILES.upload(jsonObj[ATTACHMENT_FIELD_KEY]);
        if (error) {
            errors.push(error);
        }
        else {
            resultJSON[ATTACHMENT_FIELD_KEY] = uploadFilesResult?.reduce((pre, cur) => [...pre, ...cur.data.data], []);
        }
    }

    for (const objKey in jsonObj) {
        if (jsonObj[objKey] && typeof jsonObj[objKey] === 'object' && !Array.isArray(jsonObj[objKey])) {
            const [objectKeyErrors, uploadedAttachmentPayload] = await UploadAttachments(jsonObj[objKey]);
            resultJSON[objKey] = uploadedAttachmentPayload;
            errors = errors.concat(objectKeyErrors);
        }
        else if (objKey !== ATTACHMENT_FIELD_KEY && jsonObj[objKey] && typeof jsonObj[objKey] === 'object' && Array.isArray(jsonObj[objKey])) {
            resultJSON[objKey] = await Promise.all(jsonObj[objKey].map(async (item) => {
                if (item && typeof item === 'object' && !Array.isArray(item)) {
                    const [objectKeyErrors, uploadedAttachmentPayload] = await UploadAttachments(item);
                    errors = errors.concat(objectKeyErrors);
                    item = uploadedAttachmentPayload;
                }
                return item;
            })
            );
        }
    }


    return [errors, resultJSON];
}
